<template>
  <div>
    <div v-if="isReady" class="dashboard__content"
         :class="{'full-width': showEncourage || !hasRegisterPage}"
         @click="hideSettingOption">
      <template v-if="!showEncourage">
        <template v-if="hasRegisterPage">
          <domain-list
            :isEditMode="isEditMode"
            :domains="domains"
            :currentSid="currentSid"
            :domainCategories="domainCategories"
            :hasFooter="showPeekaiNudge"
            @onDomainSelect="onDomainSelect"
          >
            <peekai-nudge
              v-if="showPeekaiNudge"
              :key="userData.userNum"
              slot="footer"
              :user-num="userData.userNum"
              :sid="currentSid"
              :domain="currentDomainName"
            />
          </domain-list>

          <div class="content--top" :class="{scrolled: !isContentHeaderScrollTop}">
            <app-header>
              <user-info-summary slot="extra"/>
            </app-header>

            <div v-if="isEditMode" class="edit-mode__notice">
              <i class="icon icon--edit-mode-notice"></i>
              <p v-html="$t('dashboard.editMode.stickyBar')"></p>
              <span class="domain-name" :class="{ 'edit-mode': isEditMode }">{{ currentDomainName }}</span>
            </div>

            <dashboard-action
              :canRegister="canRegister"
              :isEditMode="isEditMode"
              :currentDomainName="currentDomainName"
              :selectedApplies="selectedApplies"
              :searchKeyword.sync="searchKeyword"
              :hasApplies="hasApplies"
              :isSharedDomain="isSharedDomain"
              :isSPA="currentDomainIsSPA"
              :domain="currentDomain"
              :samplingRate="samplingRate"
              @onEditModeClick="onEditModeClick"
              @onEditCancelClick="onEditCancelClick"
              @onShowTrackingCodeClick="showTrackingCode = true"
              @onShowGroupMoveLayerClick="showGroupMoveLayer = true"
              @onBulkDateChange="onBulkDateChange"
              @onBulkDelete="onBulkDelete"
              @onSaveClick="onSaveClick"
              @onChangeSPA="onChangeSPA"
              @reload="reload"
            />
          </div>

          <div v-if="!hasApplies"
               class="no-result">
            <div v-if="isLoadingPageList">
              <ui-loading-circle>
                <span class="loading_notice" v-html="$t('common.loading')"></span>
              </ui-loading-circle>
            </div>
            <div v-else-if="searchKeyword.length"
                 class="no-result--wrapper">
              <div class="no-result--wrap">
                <i class="icon icon--empty"></i>
                <p class="title">{{ $t('dashboard.noApplies.searchResult.title') }}</p>
                <p>{{ $t('dashboard.noApplies.searchResult.desc') }}</p>
                <p class="keyword">'{{ searchKeyword }}'</p>
              </div>
            </div>
            <div v-else
                 class="no-apply">
              {{ $t('dashboard.noApplies.desc') }}
            </div>
          </div>

          <div v-else
               class="category-group"
               :class="{'edit-mode': isEditMode, 'scrolled': !isContentHeaderScrollTop}"
               @scroll="onContainerScroll">

            <ul>
              <apply-category v-for="(category, index) in categories" :key="index" ref="tableWrapper"
                              :class="{'edit-mode': isEditMode}"
                              :isEditMode="isEditMode"
                              :category="category"
                              :categoryIndex="index"
                              :categoryState="categoryState[currentSid][category._id]"
                              :selectedApplies="selectedApplies"
                              :searchKeyword="searchKeyword"
                              :scrollTop="scrollTop"
                              :timezone="currentDomain.timezone"
                              :timezoneName="currentDomain.timezoneName"
                              @onCategoryNameChange="onCategoryNameChange"
                              @onApplyChange="onApplyChange"
                              @onApplyMetaChange="onApplyMetaChange"
                              @onToggleSubscribeWeeklyReport="onToggleSubscribeWeeklyReport"
                              @onSortApplies="onSortApplies"
                              @onCustomSort="onCustomSort"
                              @onToggleCategory="onToggleCategory"
                              @onCheckInstallClick="onCheckInstallClick"
                              @onHideSettingOption="hideSettingOption"
              />
            </ul>
          </div>

          <group-move-layer
            v-if="showGroupMoveLayer"
            :categories="categories"
            :selectedApplies="selectedApplies"
            @onCloseClick="showGroupMoveLayer = false"
            @groupMove="onBulkMove"
          />

          <tracking-code-confirm
            v-if="showTrackingCode"
            :sid="currentDomain.sid"
            @closePopup="showTrackingCode = false"
          />

          <url-install-checker-layer v-if="installCheckerApplyData"
                                     :applyData="installCheckerApplyData"
                                     @onCheckInstall="onCheckInstall"
                                     @onCloseClick="installCheckerApplyData = null"/>

          <install-checker v-if="installCheckerData"
                           :checkerData="installCheckerData"
                           @onDone="onCheckInstallResult"/>
        </template>
        <template v-else-if="!hasCredit">
          <app-header>
            <h1 slot="extra">
              <router-link :to="{ name: 'home' }" @click.native="$router.go()">
                <i class="icon icon--logo-new"></i>
              </router-link>
            </h1>
          </app-header>
          <dashboard-no-page-no-credit/>
        </template>
        <template v-else>
          <app-header>
            <h1 slot="extra">
              <router-link :to="{ name: 'home' }" @click.native="$router.go()">
                <i class="icon icon--logo-new"></i>
              </router-link>
            </h1>
          </app-header>
          <dashboard-no-page/>
        </template>
      </template>
      <template v-if="showEncourage">
        <app-header>
          <h1 slot="extra">
            <router-link :to="{ name: 'home' }" @click.native="$router.go()">
              <i class="icon icon--logo-new"></i>
            </router-link>
          </h1>
        </app-header>
        <template v-if="isFirstLogin">
          <dashboard-no-page/>
          <dimmed v-if="isShowFirstLoginLayer">
            <encourage-plan :dashboardState="dashboardState"
                            @onCloseLayer="onFirstLoginUserStart"/>
          </dimmed>
        </template>
        <encourage-plan v-else :dashboardState="dashboardState"/>
      </template>
    </div>
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import { mapMutations, mapState } from 'vuex'
import { format } from 'date-fns'
import { bulkUpdate, getDomains, getPageListBySid, insertApply, updateSPA } from '@/api/apply'
import { getNotice } from '@/api/notice'
import { checkAid } from '@/api/funnel'
import { cloneDeep, getOffset, openContactUs, sequence, utcToLocalTimezone } from '@/libs/utils'
import { getI18n } from '@/libs/i18n'
import { STORAGE_KEY, storageManager } from '@/libs/storage-manager'
import AppHeader from '@/components/header'
import UserInfoSummary from '@/components/header/UserInfoSummary'
import DashboardAction from '@/components/dashboard/DashboardAction'
import DomainList from '@/components/dashboard/DomainList'
import PeekaiNudge from '@/components/dashboard/PeekaiNudge'
import ApplyCategory from '@/components/dashboard/ApplyCategory'
import GroupMoveLayer from '@/components/dashboard/GroupMoveLayer'
import TrackingCodeConfirm from '@/components/dashboard/TrackingCodeConfirm'
import ConfirmFunnelPage from '@/components/dashboard/ConfirmFunnelPage'
import InstallChecker from '@/components/ui/InstallChecker'
import UrlInstallCheckerLayer from '@/components/dashboard/UrlInstallCheckerLayer'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle.vue'
import EncouragePlan from '@/components/dashboard/EncouragePlan'
import DashboardNoPage from '@/components/dashboard/DashboardNoPage'
import DashboardNoPageNoCredit from '@/components/dashboard/DashboardNoPageNoCredit'
import Dimmed from '@/components/ui/Dimmed'
import AppDialog from '@/components/ui/AppDialog'
import { create } from 'vue-modal-dialogs'
import NoticePopup from '@/components/ui/NoticePopup'
import { CommonStorage, getFirstLoginCookie } from '@/libs/auth-helper'
import { validateTimezone } from '@/api/timezone'

const openDialog = create(AppDialog)
const openNotice = create(NoticePopup)
const openFunnelCheckDialog = create(ConfirmFunnelPage, 'checkData')

export default {
  name: 'Dashboard',
  components: {
    AppHeader,
    UserInfoSummary,
    DashboardAction,
    DomainList,
    PeekaiNudge,
    ApplyCategory,
    GroupMoveLayer,
    TrackingCodeConfirm,
    InstallChecker,
    UrlInstallCheckerLayer,
    EncouragePlan,
    DashboardNoPage,
    DashboardNoPageNoCredit,
    Dimmed,
    UiLoadingCircle
  },
  data () {
    return {
      isReady: false,
      domains: [],
      currentDomain: null,
      categoryData: {},
      categories: [],
      searchKeyword: '',
      showTrackingCode: false,
      showGroupMoveLayer: false,
      categoryState: {},
      hasApplies: false,
      // new
      installCheckerData: false,
      installCheckerApplyData: false,
      isContentHeaderScrollTop: true,
      isShowEncourageLayer: true,
      isShowFirstLoginLayer: true,
      funnelCheckData: null,
      dentsuClientData: null,
      scrollTop: 0,
      isLoadingPageList: false,
      isFirstLogin: 'N'
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'isABPlan',
      'isDashboardEditMode',
      'showNoPvDialog',
      'applyData'
    ]),
    isSharedDomain () {
      let isSharedDomain = false
      if (this.categories) {
        for (const category of this.categories) {
          if (category._id === '__shared__') {
            isSharedDomain = true
          }
        }
      }
      return isSharedDomain
    },
    usingPlan () {
      return this.planData.hasPlan
    },
    hasCredit () {
      return !this.userData.creditEmpty && this.userData.remainPv !== 0
    },
    dashboardState () {
      if (!this.isReady) {
        return null
      }

      let state

      if (this.isFirstLogin === 'Y') {
        state = 'firstLogin'
      } else if (this.hasRegisterPage) {
        if (this.usingPlan) {
          if (this.hasCredit) {
            state = 'page-plan-credit'
          } else {
            state = 'page-plan-noCredit'
          }
        } else {
          state = 'page-noPlan'
        }
      } else {
        if (this.usingPlan) {
          if (this.hasCredit) {
            state = 'noPage-plan-credit'
          } else {
            state = 'noPage-plan-noCredit'
          }
        } else {
          state = 'noPage-noPlan'
        }
      }
      return state
    },
    canRegister () {
      return this.usingPlan && this.hasCredit
    },
    showEncourage () {
      const targetStates = ['firstLogin', 'noPage-noPlan']
      return targetStates.includes(this.dashboardState)
    },
    useDimmed () {
      return this.isShowEncourageLayer
    },
    isEditMode () {
      return this.isDashboardEditMode
    },
    hasRegisterPage () {
      return this.domains.length
    },
    currentSid () {
      return this.currentDomain ? this.currentDomain.sid : null
    },
    currentDomainName () {
      return this.currentDomain ? this.currentDomain.domain : null
    },
    currentDomainIsSPA () {
      return this.currentDomain ? this.currentDomain.isSPA : false
    },
    domainCategories () {
      return this.currentSid ? this.categoryData[this.currentSid] : []
    },
    showPeekaiNudge () {
      return false
      // return Boolean(this.currentSid && this.currentDomainName && this.userData && this.userData.userNum != null) && this.usingPlan && !this.isABPlan && !this.isEditMode
    },
    samplingRate () {
      if (this.currentDomain && this.currentDomain.samplingRate !== null && this.currentDomain.samplingRate < 100) {
        return this.currentDomain.samplingRate
      }
      return null
    },
    allApplies () {
      let items = []

      for (const category of this.categories) {
        items = items.concat(category.applies)
      }
      return items
    },
    selectedApplies () {
      return this.allApplies.filter(item => item.meta.isChecked)
    }
  },
  watch: {
    searchKeyword (value) {
      this.setKeywordfilter()
    },
    dashboardState (value) {
      if (value === 'page-plan-noCredit' && this.showNoPvDialog) {
        const canCreateOrChange = this.planData.canCreate || this.planData.canChange

        openDialog({
          dialogType: 'plan.pvEmpty'
        }).then((response) => {
          if (response) {
            if (canCreateOrChange) {
              this.$router.push({ name: 'managePlan', params: { action: response === 'pv' ? 'chargePv' : 'changePlan' } })
            } else {
              openContactUs(this.userData)
            }
          }
          this.setShowNoPvDialog(false)
        })
      }
    }
  },
  async created () {
    this.categoryState = storageManager.getItem(STORAGE_KEY.categoryState) || {}
    this.isFirstLogin = await getFirstLoginCookie()

    this.load()
  },
  destroyed () {
    this.setIsDashboardEditMode(false)
  },
  mounted () {
    window.addEventListener('message', function (msg) {
      if (msg.data) {
        const call = msg.data.call
        const model = msg.data.model
        if (model && model.content && call && call === 'popupPreview') {
          const content = model.content
          const serviceType = model.serviceType
          const location = model.exposureLocation
          if (content && serviceType && location && serviceType === 'beusable' && location === 'dashboard') {
            const popupPreviewContainer = document.querySelector('#app > div:nth-child(2)')
            if (popupPreviewContainer) {
              popupPreviewContainer.innerHTML = ''
              const popupId = document.createElement('div')
              popupId.id = 'be-notice-background'
              popupId.className = 'notice_dialog'
              popupId.innerHTML = content
              popupPreviewContainer.append(popupId)
            }
          }
        }
      }
    })
  },
  methods: {
    ...mapMutations([
      'setIsFirstLogin',
      'setIsDashboardEditMode',
      'setCurrentDashboardCategory',
      'setShowNoPvDialog'
    ]),
    load () {
      const targetSid = this.$route.query.sid
      this.loadDomains(targetSid).then(() => {
        this.isReady = true

        this.loadNotice()
      }).catch(err => {
        console.error(err)
        this.isReady = true
      })
    },
    loadNotice () {
      const noticeParam = {
        exposureLocation: 'dashboard',
        lang: getI18n().locale
      }
      getNotice(noticeParam).then((data) => {
        if (data.length > 0) {
          const noticeList = data.sort((a, b) => {
            return a.priority > b.priority ? -1 : 1
          })
          noticeList.forEach((item) => {
            const popupId = item._id
            if (popupId) {
              const cookie = CommonStorage.getItem('noticePopup' + item._id)
              if (!cookie) {
                openNotice({ data: item }).then((response) => {
                })
              }
            }
          })
        }
      }).catch(noticeErr => {
      })
    },
    reload () {
      this.loadDomains(this.currentSid, true)
    },
    loadDomains (targetSid, forceReload) {
      return getDomains().then(items => {
        items.forEach(item => {
          item.isExpanded = false
          item.isHide = false

          if (!item.hasOwnProperty('isSPA')) {
            item.isSPA = false
          }
        })
        const sidList = items.map(item => item.sid)
        this.initState(this.categoryState, sidList, {})

        this.domains = items
        let currentDomain

        if (targetSid) {
          currentDomain = this.domains.find(item => item.sid === targetSid)
        }

        if (!currentDomain && this.domains.length) {
          currentDomain = this.domains[0]
        }

        if (currentDomain) {
          this.onDomainSelect(currentDomain, forceReload)
        }
      }).catch(err => {
        this.domains = []
        this.initState(this.categoryState, [], {})
        if (err.message !== 'No Domains found') {
          console.error(err)
        }
      })
    },
    onDomainSelect (domain, forceReload) {
      if (!forceReload && domain.sid === this.currentSid) {
        domain.isExpanded = !domain.isExpanded
        return
      }

      this.domains.forEach(item => {
        item.isExpanded = false
      })

      domain.timezone = getOffset(domain.timezoneName)

      this.currentDomain = domain
      validateTimezone({ domain: domain.domain }).then(result => {
        this.currentDomain.updatable = result.updatable
        this.currentDomain.rootDomain = result.rootDomain
        domain.isExpanded = true
      })

      let categories = this.categoryData[domain.sid]

      new Promise((resolve, reject) => {
        if (!forceReload && categories) {
          resolve(categories)
        } else {
          this.isLoadingPageList = true

          getPageListBySid(domain.sid).then(items => {
            this.initCategories(items)
            this.$set(this.categoryData, domain.sid, items)
            this.isLoadingPageList = false
            resolve(items)
          }).catch(err => {
            this.isLoadingPageList = false
            reject(err)
          })
        }
      }).then(categories => {
        this.categories = categories
        this.setKeywordfilter()
        let currentCategory = {}

        if (categories.length) {
          currentCategory = {
            categoryId: categories[0]._id,
            index: 0
          }
        }
        this.setCurrentDashboardCategory(currentCategory)
      }).catch(console.error)
    },
    initCategories (categories) {
      const domainState = this.categoryState[this.currentSid]
      const categoryNames = categories.map(item => item._id)
      this.initState(domainState, categoryNames, {
        isFolded: false,
        sortOptions: {
          column: 'pvPerMonth',
          isAsc: false
        }
      })

      categories.sort((a, b) => {
        if (b._id === '__shared__') {
          return -1
        } else if (a._id === '__shared__') {
          return 0
        } else {
          return a._id.localeCompare(b._id)
        }
      })

      for (const category of categories) {
        for (const apply of category.applies) {
          apply.meta.isHide = false
          apply.meta.isShowShareRow = false

          if (apply.meta.requestableStates && apply.meta.requestableStates.length) {
            let states = [{
              value: apply.state,
              text: this.$t(`page.state.${apply.meta.uiState}`),
              cssClass: apply.meta.uiState
            }]
            states = states.concat(apply.meta.requestableStates.map(value => {
              return {
                value,
                text: this.$t(`page.state.${value}`),
                cssClass: value
              }
            }))
            apply.meta.requestableStates = states
          } else {
            apply.meta.requestableStates = null
          }
        }

        const sortOptions = this.getCategoryState(category._id).sortOptions
        this.sortApplies(sortOptions, category.applies)
      }
      this.saveState()
    },
    sortApplies (options, applies) {
      const column = options.column
      let isAsc = options.isAsc

      applies.sort((a, b) => {
        let aValue
        let bValue

        switch (column) {
          case 'custom':
            aValue = a.customRank || 0
            bValue = b.customRank || 0
            isAsc = true
            break
          case 'meta.uiState':
            aValue = a.meta.uiState
            bValue = b.meta.uiState
            break
          case 'meta.trackingCodeChecked':
            aValue = a.meta.trackingCodeChecked
            bValue = b.meta.trackingCodeChecked
            break
          case 'startDate':
            aValue = a.startDate || a.regDate
            bValue = b.startDate || b.regDate
            break
          default:
            aValue = a[column]
            bValue = b[column]
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return isAsc ? aValue - bValue : bValue - aValue
        }

        if (aValue === undefined || aValue === null) {
          aValue = ''
        } else {
          aValue = String(aValue)
        }

        if (bValue === undefined || bValue === null) {
          bValue = ''
        } else {
          bValue = String(bValue)
        }

        let result = aValue.localeCompare(bValue)

        if (!isAsc) {
          result = -result
        }

        return result
      })
    },
    setKeywordfilter () {
      const value = this.searchKeyword.toLowerCase()
      let hasApplies = false

      for (const category of this.categories) {
        for (const apply of category.applies) {
          let isHide = false

          if (value.length) {
            isHide = !apply.pageName.toLowerCase().includes(value)

            if (!isHide) {
              hasApplies = true
            }
          } else {
            hasApplies = true
          }
          apply.meta.isHide = isHide
        }
      }
      this.hasApplies = hasApplies
    },
    onEditModeClick () {
      const categories = cloneDeep(this.categories)

      // todo: shared category remove

      for (const category of categories) {
        const categoryName = category._id
        category.isCustomSorted = false
        category.oldName = categoryName

        for (const apply of category.applies) {
          apply.deleted = false
          apply.isCategoryChanged = false
          apply.category = categoryName
          apply.meta = {
            ...apply.meta,
            isChecked: false,
            isModified: false,
            showStartDatePicker: false,
            showEndDatePicker: false,
            applyState: apply.state
          }
        }
      }
      this.categories = categories
      this.setIsDashboardEditMode(true)
    },
    onEditCancelClick () {
      this.categories = this.categoryData[this.currentSid]
      this.setIsDashboardEditMode(false)
    },
    onCategoryNameChange (category, name) {
      category._id = name

      for (const apply of category.applies) {
        apply.meta.isModified = true
        apply.category = name
      }
    },
    onApplyChange ({ apply, key, value }) {
      apply.meta.isModified = true
      apply[key] = value
    },
    onApplyMetaChange ({ meta, key, value }) {
      meta[key] = value
    },
    onToggleSubscribeWeeklyReport (apply) {
      const value = !apply.isSubscribeWeeklyReport
      insertApply({
        id: apply.id,
        isSubscribeWeeklyReport: value
      }).then(() => {
        apply.isSubscribeWeeklyReport = !apply.isSubscribeWeeklyReport
      }).catch(err => {
        console.error(err)
      })
    },
    onSortApplies (category, fieldName, isAsc) {
      const categoryName = category._id
      const sortOptions = this.getCategoryState(categoryName).sortOptions
      sortOptions.column = fieldName
      sortOptions.isAsc = isAsc
      this.sortApplies(sortOptions, category.applies)
      this.saveState()
    },
    onCustomSort (category) {
      category.isCustomSorted = true
    },
    updateCustomSortOptions (categoryName) {
      const sortOptions = this.getCategoryState(categoryName).sortOptions

      if (sortOptions) {
        sortOptions.column = 'custom'
        sortOptions.isAsc = true
      }
    },
    onBulkDateChange (dateType, value) {
      this.selectedApplies.forEach(item => {
        item[dateType] = value
        item.meta.isChecked = false
        item.meta.isModified = true
      })
    },
    onBulkDelete () {
      this.selectedApplies.forEach(item => {
        item.deleted = true
        item.meta.isChecked = false
        item.meta.isModified = true
      })
    },
    onBulkMove (newCategoryName) {
      this.showGroupMoveLayer = false
      let targetCategory = this.categories.find(item => item._id === newCategoryName)

      if (!targetCategory) {
        targetCategory = {
          _id: newCategoryName,
          applies: []
        }
        this.categories.push(targetCategory)
      }

      for (const category of this.categories) {
        const applies = category.applies

        for (let i = applies.length - 1; i >= 0; i--) {
          const apply = category.applies[i]

          if (apply.meta.isChecked && apply.category !== newCategoryName) {
            const applyToMove = applies.splice(i, 1)[0]
            applyToMove.category = newCategoryName
            applyToMove.isCategoryChanged = true
            applyToMove.meta.isModified = true
            applyToMove.meta.isChecked = false
            targetCategory.applies.push(applyToMove)
          }
        }
      }

      for (let i = this.categories.length - 1; i >= 0; i--) {
        const category = this.categories[i]

        if (!category.applies.length) {
          this.categories.splice(i, 1)
        }
      }
    },
    checkFunnelItem (item) {
      return new Promise((resolve, reject) => {
        const isDeleted = item.deleted
        const isStopped = item.meta.applyState === 'run' && item.state === 'stop'
        let mode

        if (isDeleted) {
          mode = 'delete'
        } else if (isStopped) {
          mode = 'stop'
        }

        if (!mode) {
          return resolve()
        }

        checkAid(item.id).then(data => {
          if (data && data.length) {
            openFunnelCheckDialog({
              pageName: item.pageName,
              mode,
              items: data
            }).then(result => {
              if (!result) {
                switch (mode) {
                  case 'delete':
                    this.$set(item, 'deleted', false)

                    if (isStopped) {
                      this.$set(item, 'state', 'run')
                    }
                    break
                  case 'stop':
                    this.$set(item, 'state', 'run')
                    break
                }
              }
              return resolve()
            })
          } else {
            return resolve()
          }
        }).catch(err => {
          reject(err)
        })
      })
    },
    onSaveClick () {
      const modified = this.allApplies.filter(item => item.meta.isModified)
      sequence(modified, this.checkFunnelItem).then(() => {
        const postData = modified.map(item => {
          let endDate = item.endDate
          if (endDate) {
            if (typeof item.endDate === 'string') {
              endDate = format(utcToLocalTimezone(endDate, item.timezoneName), 'YYYY-MM-DD')
            } else {
              endDate = format(item.endDate, 'YYYY-MM-DD')
            }
          }

          const data = {
            id: item.id,
            pageName: item.pageName,
            state: item.state,
            startDate: item.startDate ? format(item.startDate, 'YYYY-MM-DD') : null,
            endDate: item.endDate ? endDate : null,
            isSubscribeWeeklyReport: item.isSubscribeWeeklyReport,
            deleted: item.deleted,
            customRank: item.customRank,
            isCategoryChanged: item.isCategoryChanged,
            category: item.category,
            timezone: item.timezone,
            timezoneName: item.timezoneName
          }
          return data
        })

        bulkUpdate({ data: postData }).then(data => {
          if (data.update_fail.length) {
            const isAllFailed = !data.update_success.length
            const message = isAllFailed ? 'failed all!' : 'failed some'
            alert(message)
          }

          for (const category of this.categories) {
            if (category.oldName !== category._id) {
              this.updateCategoryStateName(category.oldName, category._id)
            }

            if (category.isCustomSorted) {
              this.updateCustomSortOptions(category._id)
            }
          }
          this.saveState()
          this.setIsDashboardEditMode(false)
          this.reload()
        }).catch(err => {
          console.error(err)
        })
      }).catch(() => {
        openDialog({
          dialogType: 'common.error'
        })
      })
    },
    onChangeSPA () {
      const params = {
        sid: this.currentDomain.sid,
        isSPA: !this.currentDomain.isSPA
      }
      updateSPA(params).then(res => {
        this.currentDomain.isSPA = !this.currentDomain.isSPA
      })
    },
    saveState () {
      storageManager.setItem(STORAGE_KEY.categoryState, this.categoryState)
    },
    initState (state, usedKeys, defaultState) {
      const savedKeys = Object.keys(state)

      for (const savedKey of savedKeys) {
        let hasData = false

        for (const usedKey of usedKeys) {
          if (savedKey === usedKey) {
            hasData = true
            break
          }
        }

        if (!hasData) {
          this.$delete(state, savedKey)
        }
      }

      for (const usedKey of usedKeys) {
        if (!state[usedKey]) {
          this.$set(state, usedKey, cloneDeep(defaultState))
        }
      }
    },
    getCategoryState (categoryName) {
      return this.categoryState[this.currentSid][categoryName]
    },
    updateCategoryStateName (oldName, newName) {
      let state = this.categoryState[this.currentSid][oldName]

      if (state) {
        state = cloneDeep(state)
        this.$delete(this.categoryState[this.currentSid], oldName)
        this.$set(this.categoryState[this.currentSid], newName, state)
      }
    },
    onToggleCategory (category) {
      const categoryState = this.getCategoryState(category._id)
      categoryState.isFolded = !categoryState.isFolded
      this.saveState()
    },
    onContainerScroll (event) {
      this.isContentHeaderScrollTop = event.currentTarget.scrollTop === 0
      this.scrollTop = event.target.scrollTop
    },
    onCheckInstallClick (item) {
      if (['exact', 'simple'].includes(item.matchType)) {
        this.installCheckerData = {
          aid: item.id
        }
      } else {
        this.installCheckerApplyData = item
      }
    },
    onCheckInstall (aid, url) {
      this.installCheckerApplyData = null
      this.installCheckerData = {
        aid,
        url
      }
    },
    onCheckInstallResult (needReload) {
      this.installCheckerData = null

      if (needReload) {
        this.reload()
      }
    },
    onFirstLoginUserStart () {
      this.isShowFirstLoginLayer = false
      // this.setIsFirstLogin(false)
      // setFirstLoginCookie('N')
    },
    hideSettingOption () {
      const settingOptionEl = document.querySelectorAll('.data__setting .setting__option--wrap')
      for (const item of settingOptionEl) {
        item.style.display = 'none'
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    let canLeave = true

    if (this.isEditMode) {
      canLeave = confirm(this.$t('common.pageLeave'))
    }

    if (canLeave) {
      next()
    } else {
      next(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.dentsu-bridge--layer,
.ui-change--layer {
  &:before {
    position: absolute;
    top: 0;
    left: 0;
    content: '';
    display: inline-block;
    width: 100%;
    height: 100%;
    background-image: url(../../../assets/img/signin/background_img_1.jpg);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  }
}

.dashboard__content {
  &.full-width {
    width: 100%;

    .header--wrap {
      box-sizing: border-box;
      padding-left: 15px;
      padding-right: 10px;
    }
  }

  & > .no-result {
    width: calc(100vw - 210px);
    height: calc(100vh - 120px);
    overflow-y: auto;
    text-align: center;
    font-size: 14px;
    color: #767676;
    position: relative;

    .no-result--wrapper {
      height: 100%;
      width: 100%;
      display: table;
    }

    .no-result--wrap {
      display: table-cell;
      vertical-align: middle;
      text-align: center;
      font-size: 14px;
      color: #767676;
      padding-bottom: 120px;
    }

    .no-apply {
      @include positionXYcenter;
      padding-bottom: 120px;
    }

    .title {
      margin: 15px auto;
      font-size: 20px;
      font-weight: 500;
      color: #2f2f2f;
    }
    .keyword {
      font-size: 15px;
      line-height: 24px;
      font-weight: 500;
      color: #ff8400;
      margin: 30px auto 0;
      width: 524px;
      word-break: break-all;
    }

    .loading.position {
      left: calc(50% + 105px);
    }
  }
  .content--top {
    z-index: 2;
    position: relative;
    float: left;
    top: 0;
    /*left: 211px;*/
    min-width: 754px;
    width: calc(100% - 211px);
    padding-left: 30px;
    padding-right: 30px;
    box-sizing: border-box;
    height: 154px;

    .edit-mode__notice {
      min-width: 960px;
      width: 100%;
      height: 40px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
      background-color: #d1ecff;
      border-bottom: solid 1px #b5defa;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 10;
      box-sizing: border-box;
      padding-left: 30px;

      p {
        font-size: 12px;
        color: #289eef;
        line-height: 40px;
        font-weight: 500;
      }

      .icon {
        position: absolute;
        top: 50%;
        left: 9px;
        transform: translateY(-50%);
      }
      span {
        position: absolute;
        top: 50%;
        right: 30px;
        transform: translateY(-50%);
        font-size: 14px;
        font-weight: 600;
        color: #289eef;
      }
    }

    &.scrolled {
      background-color: #f4f4f1;
      &:after {
        content: "";
        display: block;
        width: calc(100% + 60px); // 60px: left padding + right padding of parent
        height: 12px;
        background-image: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.18),
            rgba(0, 0, 0, 0)
        );
        margin-left: -30px; // left padding of parent
        pointer-events: none;
      }
    }
  }
  .dashboard__button--wrap {
    position: relative;
    margin-top: 59px;
    padding-bottom: 15px;

    &.edit-mode {
      margin-top: 27px;
      padding-bottom: 17px;

      .button--wrap {
        &:before {
          content: "";
          display: inline-block;
          top: 50%;
          left: -6px;
          width: 1px;
          height: 20px;
          border-left: 1px solid #d7d7d7;
          position: absolute;
          transform: translateY(-50%);
        }
      }
    }
    .domain-name {
      font-size: 26px;
      font-weight: 600;
      color: #444444;
    }

    .button--wrap {
      position: absolute;
      top: calc(50% - 11px);
      right: 0;
      transform: translateY(-50%);

      li {
        float: left;

        button {
          @include buttonTypeNormal;
          position: relative;
          padding-left: 40px;
          font-size: 14px;
          font-weight: 600;
          margin-left: 5px;

          .icon {
            position: absolute;
            top: 50%;
            left: 15px;
            transform: translateY(-50%);
          }

          &.btn__search {
            width: 38px;
            height: 38px;
            border-radius: 20px;
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            box-sizing: border-box;
            padding: 0;
            min-width: inherit;

            .icon {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            }

            &:hover {
              @include hoverStyle;
            }
          }

          &.btn__register,
          &.btn__edit__save {
            background-color: $brandColor;
            color: #fff;
            overflow: hidden;
            &:hover:before {
              content: "";
              display: inline-block;
              width: 100%;
              height: 100%;
              position: absolute;
              top: 0;
              left: 0;
              background-color: rgba(0, 0, 0, 0.16);
            }
          }
          &.btn__tracking-code,
          &.btn__edit-mode,
          &.btn__edit__cancel {
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            &:hover {
              @include hoverStyle;
            }
            .icon {
              background-color: transparent;
            }
          }

          &.btn__edit__save,
          &.btn__edit__cancel {
            width: 100px;
            height: 38px;
            line-height: 36px;
            text-align: right;
          }
        }
      }

      .input__search {
        position: relative;
        #dashboardSearchInput {
          width: 240px;
          height: 38px;
          line-height: 36px;
          border-radius: 20px;
          border: solid 1px #bbbbbb;
          background-color: #ffffff;
          padding-left: 18px;
          padding-right: 26px;
          box-sizing: border-box;
          font-size: 14px;
          color: #2f2f2f;

          &:focus {
            border: solid 1px #289eef;
          }
        }

        .icon {
          position: absolute;
          top: 50%;
          right: 11px;
          transform: translateY(-50%);
        }
      }
    }

    .bulk-control--wrap {
      display: inline-block;
      z-index: 1;
      height: 46px;
      border-radius: 8px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.16);
      background-color: #ffffff;
      font-size: 13px;
      color: #444444;
      padding: 4px;
      box-sizing: border-box;
      opacity: 0;
      pointer-events: none;

      &.isShow {
        opacity: 1;
        pointer-events: all;
      }

      & > ul {
        height: 100%;
        white-space: nowrap;

        & > li {
          position: relative;
          display: inline-block;
          height: 100%;
          padding-left: 34px;
          padding-right: 6px;
          cursor: pointer;
          margin-right: 13px;

          &.disabled {
            opacity: 0.3;
            pointer-events: none;
          }

          &.state {
            padding-right: 26px;
          }

          &:hover {
            @include hoverStyle;
            border-radius: 3px;
          }

          &:after {
            content: "";
            display: inline-block;
            width: 1px;
            height: 16px;
            border-right: 1px solid #d7d7d7;
            position: absolute;
            top: 50%;
            right: -7px;
            transform: translateY(-50%);
          }

          &:nth-last-of-type(1) {
            margin-right: 0;
            &:after {
              content: none;
            }
          }

          .icon {
            position: absolute;
            top: 50%;
            left: 9px;
            transform: translateY(-50%);

            &.icon--toggle-slide16 {
              left: auto;
              right: 4px;
            }
          }
          span {
            display: inline-block;
            margin-left: 5px;
            line-height: 38px;
            font-weight: 400;
          }
        }
      }

      .state--wrap {
        position: absolute;
        bottom: -85px;
        left: 0;
        z-index: 1;
        .select__option--wrap {
          border-radius: 6px;
          box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(0, 0, 0, 0.25);
          background-color: #ffffff;
          overflow: hidden;
          .select__option {
            height: 38px;
            line-height: 38px;
            font-size: 14px;
            min-width: 108px;
            padding-left: 15px;
            padding-right: 15px;
            background-color: #fff;
            &:hover {
              background-color: #ebebeb;
            }
          }
        }
      }
    }
  }
  .category-group {
    position: relative;
    margin-top: 136px;
    box-sizing: border-box;
    height: calc(100vh - 134px);
    overflow-y: overlay;
    scrollbar-gutter: stable;
    padding-top: 1px;
    padding-left: 30px;
    padding-right: 16px;

    &.scrolled {
      top: -12px; // gradient height
    }

    &.edit-mode {
      &.scrolled {
        padding-bottom: 300px;
      }
    }
  }
  .category-group::-webkit-scrollbar-track {
      background-color: transparent;
  }
  .category-group::-webkit-scrollbar-thumb {
      border-radius: 10px;
      background-color: darkgray;
      background-clip: padding-box;
      border: 3px solid transparent;
      &:hover {
        background-color: gray;
      }
  }
  .category-group::-webkit-scrollbar {
      display: block;
      width: 14px;
  }
}
</style>
